import { memo, useCallback, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import type { EarlyStageVerificationStatus } from "../../api/earlyStageMembershipApi";
import { selectEarlyStagePriceTier } from "../../api/earlyStageMembershipApi";

interface EarlyStageApprovedStepProps {
  status: EarlyStageVerificationStatus | null;
  onContinue: (monthlyFeeGbp: number) => void;
  onStatusUpdated?: (status: EarlyStageVerificationStatus) => void;
}

function EarlyStageApprovedStepInner({
  status,
  onContinue,
  onStatusUpdated,
}: EarlyStageApprovedStepProps) {
  const savedTier = (status?.priceTier ?? "").trim();
  const [loading, setLoading] = useState(false);

  const handleContinue = useCallback(async () => {
    setLoading(true);
    try {
      // Approved Young Professional flow: default to £2/month automatically.
      if (savedTier !== "Standard2" && status?.canSelectPriceTier) {
        const nextStatus = await selectEarlyStagePriceTier("Standard2");
        onStatusUpdated?.(nextStatus);
      }
      onContinue(2);
    } catch {
      toast.error("Could not continue. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onContinue, onStatusUpdated, savedTier, status?.canSelectPriceTier]);

  return (
    <div
      className="d-flex flex-column gap-4 p-4 rounded-4 bg-white"
      style={{ minHeight: 320 }}
    >
      <div>
        <h5 className="mb-2">Your Young Professional rate is approved</h5>
        <p className="text-muted small mb-0">
          An admin has verified your application. You can now continue registration and
          complete payment.
        </p>
      </div>

      <Alert variant="success" className="mb-0">
        <strong>You are approved.</strong> Continue to terms and payment.
      </Alert>

      <Card className="border-0 bg-light">
        <Card.Body className="small text-muted mb-0">
          Your approved Young Professional discounted rate (£2/month) applies
          from your first billing cycle after payment.
        </Card.Body>
      </Card>

      <div className="d-flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={loading}
          onClick={() => void handleContinue()}
        >
          {loading ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                aria-hidden
              />
              Saving…
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </div>
    </div>
  );
}

export default memo(EarlyStageApprovedStepInner);
